import * as React from "react";
import { type Override } from "../../../types/internal/utility";
import CurrencyInput from "../Forms/CurrencyInput";
import PercentageInput from "../Forms/PercentageInput";
import { type BoundsType } from "./TouchNumbers";
import { TouchNumbersWrapper } from "./TouchNumbersWrapper";

interface Props extends Override<Omit<React.HTMLProps<HTMLInputElement>, "ref">, {
    onChange: (value: string) => void;
}> {
    inputRef?: React.Ref<HTMLInputElement>;
    input?: "normal" | "currency" | "percentage";
    bounds?: {
        max: BoundsType;
        min: BoundsType;
        decimalPlaces?: number;
    };
    value?: string;
    touchEditing?: string;
    touchValue?: number;
    onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
}

interface State {
    value: string;
}

export class EmbeddedTouchNumbers extends React.Component<Props, State> {
    public state = {
        value: typeof this.props.value === "undefined" ? "" : this.props.value,
    };

    protected onOpen: (open: boolean) => void;

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(prevProps.value !== this.props.value) {
            this.setState({
                value: typeof this.props.value === "undefined" ? "" : this.props.value,
            });
        }
    }

    protected showNumbers = (): void => {
        this.onTouchOpen(true);
    };

    protected handleChange = (e: React.FormEvent<HTMLInputElement> | number | string): void => {
        if(typeof e !== "number" && typeof e !== "string") {
            e = e.currentTarget.value;
        }

        this.setState({
            value: e.toString(),
        });

        this.props.onChange(e.toString());
    };

    protected handleClick = (e: React.MouseEvent<HTMLInputElement>): void => {
        if(typeof this.props.onClick === "function") {
            this.props.onClick(e);
        }
    };

    protected handleKeyDown = (): void => {
        if(typeof this.onOpen === "function") {
            this.onOpen(false);
        }
    };

    public prepareOpen = (onOpen: (open: boolean) => void): void => {
        this.onOpen = onOpen;
    };

    protected onTouchOpen = (open: boolean): void => {
        if(typeof this.onOpen === "function") {
            this.onOpen(open);
        }
    };

    public render(): React.ReactNode {
        const {
            input,
            inputRef,
            bounds,
            onChange,
            onClick,
            value,
            ...props
        } = this.props;

        let InputComponent: React.ComponentType<React.ComponentPropsWithRef<any>> | string = "input";

        if(input === "currency") {
            InputComponent = CurrencyInput;
        } else if(input === "percentage") {
            InputComponent = PercentageInput;
        }

        return (
            <TouchNumbersWrapper
                input={input}
                value={this.state.value}
                onChange={this.handleChange}
                prepareOpen={this.prepareOpen}
                confirmText="OK"
                bounds={bounds}
                ignoreForMobile
            >
                {(ref: React.RefObject<HTMLDivElement>): React.ReactNode => (
                    <div ref={ref} className="embedded-touch-numbers">
                        <InputComponent
                            {...props}
                            ref={this.props.inputRef}
                            onClick={this.handleClick}
                            onChange={this.handleChange}
                            onKeyDown={this.handleKeyDown}
                            onTouchStart={this.showNumbers}
                            value={this.state.value}
                        />
                    </div>
                )}
            </TouchNumbersWrapper>
        );
    }
}

export default React.forwardRef((props: Props, ref: React.Ref<HTMLInputElement>) => {
    return (
        <EmbeddedTouchNumbers
            {...props}
            inputRef={ref}
        />
    );
});
